<script setup>
import { useFocus } from "@vueuse/core";
import { vResizeObserver } from "@vueuse/components";
import { ref, computed } from "vue";
import { cn, fieldHasError } from "@/lib/utils";
import FeedbackMessage from "@/Components/forms/FeedbackMessage.vue";

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  value: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  class: { type: null, required: false },
  errorMessage: {
    type: String,
    default: "This field is required",
  },
  errors: {
    type: Object,
    required: true,
  },
  field: {
    type: String,
    required: true,
  },
});

const formattedLabel = computed(() => {
  return props.label.replace(/\*/g, '<span class="text-red-600 font-black" style="-webkit-text-stroke: 0.5px red; font-size: 1.1em;">*</span>');
});

/** @type {HTMLDivElement} */
const inputContainer = ref();

// Computed height of the inputContainer element
let inputContainerHeight = ref(0);

/** @type {HTMLInputElement} */
const input = ref();
const setInputRef = (el) => {
  input.value = el;
};

const { focused } = useFocus(input);

const onResizeObserver = () => {
  inputContainerHeight = inputContainer.value?.clientHeight;
};
</script>

<template>
  <div
    v-resize-observer="onResizeObserver"
    class="w-full flex flex-col"
    :class="cn(props.class)"
  >
    <div
      class="w-full relative border-b border-brand-dark-blue"
      :class="
        cn(
          { 'h-[3.2rem]': type === 'text' || type === 'email' },
          { 'h-full min-h-16': type === 'textarea' },
        )
      "
    >
      <label
        :style="[
          (focused || props?.value?.length > 0) && type === 'textarea'
            ? { bottom: `${inputContainerHeight - 27}px` }
            : {},
        ]"
        :class="
          cn(
            'absolute left-0 z-0 uppercase select-none transition-all duration-200 h-9 pt-3 px-1 leading-none',
            {
              'text-xs text-brand-dark-blue/50':
                focused || props?.value?.length > 0,
              'bottom-5':
                (focused || props?.value?.length > 0) &&
                (type === 'text' || type === 'email'),
              'text-sm transform bottom-1':
                !focused && props?.value?.length === 0,
            },
          )
        "
        v-html="formattedLabel"
      ></label
      >
      <div
        ref="inputContainer"
        class="z-10 absolute text-sm px-1 flex left-0 right-0 bottom-0"
        :class="
          cn(
            { 'h-8': type === 'text' || type === 'email' },
            { 'h-full min-h-16 pt-5': type === 'textarea' },
          )
        "
      >
        <slot name="input" :set-ref="setInputRef" />
      </div>
    </div>
    <FeedbackMessage
      v-if="fieldHasError(errors, field)"
      :error="true"
      :message="errorMessage"
    />
  </div>
</template>

<style scoped lang="postcss"></style>
